import { Text } from '@clay-run/glaze/text';
import type { StepDraft } from '@shared/flow/enrollment';

import { useGongFlowStepsForced } from '../../EnrollQueue/gong-flow-queries';
import type { StepOverrideSnapshot } from '../../EnrollQueue/StepOverrideEditor';
import type { StagedStepCopy } from '../../EnrollQueue/state/staged-play-config';
import { LeadGongControls } from './LeadGongControls';
import { StepRow } from './StepRow';

/**
 * The V2 rail's step-editor SECTION (plan 2026-09-30-002, U3 step 4) — the
 * live-steps fetch, the error posture and the header decision, wrapped around
 * N `StepRow`s. `StepRow` is the per-step view and knows none of this; this
 * component is `StepOverrideEditor`'s V2 counterpart minus the heading (which
 * belongs to whichever scope mounts it: `BulkEditor` renders the batch
 * heading, and a lead's rows sit under `EnrollmentItemEditor`'s own chrome).
 *
 * ONE component for BOTH scopes (D6). What differs is only what the caller
 * passes:
 *
 *  - **batch** (`BulkEditor`): `itemId` is the caller's own pseudo-id, there
 *    are no persisted overrides (`overrideFor` omitted — a batch default is
 *    staged client-side, never a `step_drafts` row), and the draft callbacks
 *    point at `useBatchStepBuffer`.
 *  - **lead** (`LeadExpandedBody` via `EnrollmentItemEditor`'s `stepsSection`):
 *    `overrideFor` reads `item.step_drafts`, the draft callbacks point at
 *    `EnrollQueueContext`'s per-item store, and `batchStepCopy` is the batch
 *    rung — present only for a bulk-follow-eligible lead (D24/D34; the gate
 *    lives at the call site, since only it holds the `EnrollmentItem`).
 *
 * Two postures are deliberate and copied from V1's `GongFlowStepsSection`:
 *
 *  1. **Loading renders NOTHING** — no skeleton, no partial editor. A row
 *     seeded from absent step defaults would show blank copy the rep could
 *     type over, and blank copy is meaningful to Gong (blank = Gong runs its
 *     own script), so a blank-by-accident field is a real send hazard.
 *  2. **An endpoint failure renders the alert and nothing else in this
 *     section** (E16), at either scope. It does NOT propagate: the rest of
 *     the Bulk Editor / lead editor stays usable and Send is still allowed —
 *     a flow whose steps could not be listed still enrolls fine, it just
 *     cannot be step-overridden this minute.
 */

export interface StepRowsProps {
  /** Which rung these rows edit — threaded to every `StepRow` (labels + expander name). */
  scope: 'lead' | 'batch';
  /** The Gong flow whose live steps these rows render. */
  flowId: string;
  /** `seedKey`/aria namespace only; batch scope passes its caller's pseudo-id. */
  itemId: number;
  /** `group.ui.body_format` — `'rich'` mounts the formatting toolbar. */
  bodyFormat?: 'plain' | 'rich';
  /** `readOnly || frozen || busy` — every control in every row goes inert. */
  disabled: boolean;
  /** The batch rung, keyed `String(step_order)`. Lead scope only, and only when the lead is bulk-follow-eligible (D24). */
  batchStepCopy?: Readonly<Record<string, StagedStepCopy>>;
  /**
   * U4b fix (plan 2026-09-30-002-fix) — the cool-off checkbox + owner note
   * V1's `GongFlowStepsSection` renders beneath its step editor
   * (`EnrollmentItemEditor.tsx:449-470`), which this component never ported.
   * Lead scope only: `BulkEditor` (batch scope) omits this prop entirely and
   * renders byte-identically to before — there is no persisted
   * `cool_off_override` at batch scope to toggle. Rendered only once
   * `stepsQuery.data` resolves (same "loading renders nothing" posture as
   * the step rows themselves), using that same response's
   * `flow_owner_name` for the owner note.
   */
  coolOff?: {
    checked: boolean;
    disabled: boolean;
    disabledTitle?: string;
    onToggle: (next: boolean) => void;
    actingRepEmail: string | null;
  };
  /**
   * This scope's PERSISTED override for one step (`item.step_drafts`), or
   * `undefined`. Omitted at batch scope, which has no persisted rung at all.
   */
  overrideFor?: (stepNumber: number) => StepDraft | undefined;
  /** See `StepRowProps` — must be referentially stable while unchanged (`useSyncExternalStore`). */
  localDraftFor: (stepNumber: number) => { values: StepOverrideSnapshot | null } | null;
  /** See `StepRowProps` — same stability requirement. */
  saveErrorFor: (stepNumber: number) => string | null;
  /** See `StepRowProps`. */
  subscribeStep: (stepNumber: number, onChange: () => void) => () => void;
  /** Fires on every settled keystroke with the FULL effective snapshot. */
  onEditStepOverride: (stepNumber: number, stepType: string, values: StepOverrideSnapshot) => void;
  /** `Use flow default`. */
  onResetStepOverride: (stepNumber: number, stepType: string) => void;
}

export function StepRows({
  scope,
  flowId,
  itemId,
  bodyFormat,
  disabled,
  batchStepCopy,
  coolOff,
  overrideFor,
  localDraftFor,
  saveErrorFor,
  subscribeStep,
  onEditStepOverride,
  onResetStepOverride,
}: StepRowsProps) {
  // The SAME forced-refetch hook V1's per-lead and batch editors use, so no
  // two surfaces in one open section can disagree about the flow's current
  // default copy. `BulkEditor` calls it too (its buffer needs `liveSteps` for
  // the mount-time prune) — react-query dedupes both to one cache entry and
  // one in-flight request.
  const stepsQuery = useGongFlowStepsForced(flowId);

  if (stepsQuery.isLoading) return null;
  if (stepsQuery.isError || !stepsQuery.data) {
    return (
      <Text role="alert" size="xs" variant="critical">
        Flow steps unavailable — try again shortly.
      </Text>
    );
  }

  const { steps, flow_owner_name: flowOwnerName } = stepsQuery.data;
  // E17 — a 1-step flow has nothing to disambiguate, so its fields render
  // inline with no collapsed header at all.
  const showHeader = steps.length > 1;

  return (
    <div className="mt-2 grid gap-1">
      {steps.map(step => (
        <StepRow
          key={step.step_order}
          scope={scope}
          itemId={itemId}
          step={step}
          override={overrideFor?.(step.step_order)}
          localDraftFor={localDraftFor}
          saveErrorFor={saveErrorFor}
          subscribeStep={subscribeStep}
          batchStepCopy={batchStepCopy}
          onEditStepOverride={onEditStepOverride}
          onResetStepOverride={onResetStepOverride}
          disabled={disabled}
          bodyFormat={bodyFormat}
          showHeader={showHeader}
        />
      ))}
      {coolOff && (
        <LeadGongControls
          itemId={itemId}
          checked={coolOff.checked}
          disabled={coolOff.disabled}
          disabledTitle={coolOff.disabledTitle}
          onToggle={coolOff.onToggle}
          actingRepEmail={coolOff.actingRepEmail}
          flowOwnerName={flowOwnerName}
        />
      )}
    </div>
  );
}
